import { Link } from "react-router-dom";
import { ArrowLeft, Wrench } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "February 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors" data-testid="privacy-back-link">
            <ArrowLeft weight="bold" className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Wrench weight="bold" className="w-4 h-4 text-white" />
            </div>
            <div className="font-display font-bold text-sm leading-none">DYNAMIC DIAG</div>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-10 fade-up" data-testid="privacy-policy-content">
        <div className="font-mono-tech text-xs text-primary tracking-widest uppercase">// legal</div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight mt-2">Privacy Policy</h1>
        <div className="text-sm text-muted-foreground mt-2 font-mono-tech">Effective: {EFFECTIVE_DATE} · JLDynamic LLC</div>

        <section className="prose prose-invert max-w-none mt-8 space-y-8 text-white/85 leading-relaxed">

          <div>
            <h2 className="font-display text-2xl text-white">1. Introduction and Data Collection</h2>
            <p className="mt-2">
              Welcome to Dynamic Diag.io, a dedicated car diagnostic platform managed by <strong>JLDynamic LLC</strong>.
              We believe in strict data minimization and absolute transparency. We only collect the data necessary to
              provide you with accurate vehicle diagnostics and secure access to your account.
            </p>
            <h3 className="font-display text-lg text-white mt-4">Information We Collect</h3>
            <ul className="list-disc list-outside pl-6 space-y-2 mt-2">
              <li><strong>Basic Login Information:</strong> your username, email address, and authentication credentials required to securely manage your account.</li>
              <li><strong>User-Accessible Data:</strong> vehicle information, error codes, and diagnostic logs that you input, upload, or generate while utilizing our platform. You retain full visibility and access to this data.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-white">2. Exclusive Use of Data</h2>
            <p className="mt-2">Our commitment to your privacy is absolute: your data is used exclusively for car diagnostics.</p>
            <ul className="list-disc list-outside pl-6 space-y-2 mt-2">
              <li>We use your login information solely to authenticate your identity and secure your account.</li>
              <li>We process your user-accessible data strictly to generate diagnostic insights, troubleshoot vehicle issues, and maintain your service history.</li>
              <li><strong>No Data Selling:</strong> JLDynamic LLC does not sell, lease, rent, or trade your personal or vehicle data to third parties for any reason.</li>
              <li>We do not utilize your information for targeted advertising or secondary data brokering.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-white">3. Third-Party Service Providers</h2>
            <p className="mt-2">
              To deliver advanced diagnostic capabilities and reliable uptime, we integrate with trusted third-party
              infrastructure and AI providers. These providers only process data to the extent required to perform
              their functions.
            </p>

            <h3 className="font-display text-lg text-white mt-5">Google Gemini (AI Diagnostic Engine)</h3>
            <p className="mt-2">
              Dynamic Diag.io is powered by Google Gemini models, which analyze vehicle data to return accurate
              diagnostic insights. When you submit a diagnostic request, the relevant data is securely transmitted to
              Google's APIs.
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2 mt-2">
              <li>Google processes prompts and responses for standard safety, abuse detection, and legal compliance.</li>
              <li>As an enterprise user of the Gemini API / Vertex AI, Google's terms explicitly state that they do not use customer prompts or responses to train or improve their underlying foundational AI models.</li>
              <li>Google may log operational data, such as token usage and error logs, and retain API requests transiently to facilitate the service.</li>
            </ul>

            <h3 className="font-display text-lg text-white mt-5">Emergent (Hosting Infrastructure)</h3>
            <p className="mt-2">Our website and backend systems are securely hosted by Emergent.</p>
            <ul className="list-disc list-outside pl-6 space-y-2 mt-2">
              <li>When you access Dynamic Diag.io, Emergent automatically collects standard technical data — such as your IP address, browser type, operating system, and timestamp data — to ensure the website functions properly and remains secure against malicious activity.</li>
              <li>Emergent's privacy policies mandate that they do not sell personal information to third parties.</li>
              <li>Emergent retains this technical data only for as long as necessary to fulfill operational, security, and legal obligations, after which it is anonymized or deleted.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-white">4. Security and User Rights</h2>
            <p className="mt-2">
              JLDynamic LLC implements robust, industry-standard security measures to protect your login credentials
              and diagnostic history from unauthorized access. Because we believe you own your data, you have the
              right to:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2 mt-2">
              <li><strong>Access and Export:</strong> view and download your complete diagnostic history directly from your user dashboard at any time.</li>
              <li><strong>Update and Correct:</strong> modify your account details or vehicle profiles as needed.</li>
              <li><strong>Delete:</strong> permanently erase your account and all associated diagnostic data from our active systems upon request.</li>
            </ul>
          </div>

          <div className="border-t border-border pt-6">
            <p>
              If you have any questions regarding this privacy policy or wish to exercise your data privacy rights,
              please contact JLDynamic LLC at{" "}
              <a href="mailto:admin@jldynamic.com" className="text-primary hover:underline">admin@jldynamic.com</a>.
            </p>
          </div>

        </section>

        <div className="mt-10 text-xs text-muted-foreground font-mono-tech">© JLDynamic LLC · Dynamic Diag</div>
      </article>
    </div>
  );
}
